//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

/* NOTE: This file has been partially converted to typescript
    - Some Types have been filled in with `object`, `void`, or other stub types
    - If you are the first typescript user of one of these APIs, please fill in appropriate types!
*/
import angular, {
  IHttpService,
  ILogService,
  IPromise,
  IWindowService,
} from 'angular';
import { IAbortablePromise, IPromiseService } from 'pinsights-js-shared';
import _ from 'underscore';

import { getRightsRequestStatuses } from '@apis/gatewayMediaApi';
import { IUidResponse } from '@models/IDeetsModel';
import IItemMetadata, { IRightsRequestState } from '@models/IItemMetadata';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import { getHighestQualityAvailableVideoUrl } from '@utils/meds';
import { RIGHTS_STATUS } from '@apis/rights-management/rightsManagementApiModels';
import Cuid from '@models/Cuid';
import { getMediaItemsByCuid } from '@apis/media-library/mediaLibraryApi';
import {
  ExistsWithTikTokNetworkItem,
  ExistsWithYouTubeNetworkItem,
  isDefinedWithTikTokNetworkItem,
  isDefinedWithYouTubeNetworkItem,
} from '@models/INetworkItem';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import {
  searchBvCatalogProducts,
  updateBvProductPdpUrl,
} from '@apis/universal-image-actions/universalImageActionsApi';
import {
  SamplingCatalogProductsRequest,
  SamplingCatalogProductsResponse,
} from '@apis/campaign-requests/sampling/products/samplingProductsApiV2Model';
import { initiateRightsRequest } from '@apis/rights-management/rightsManagementApi';
import {
  RightsRequestCommunicationType,
  RightsRequestCreationMethod,
  RightsRequestMessageSource,
} from '@apis/rights-management/rightsManagementApiModels';
import { SocialNetwork } from '@models/SocialNetwork';
import { Locales } from 'app/constants/locales/locale';

const trackingParamLabels = {
  google: {
    name: 'Google Analytics',
    label: 'Default Google Analytics Parameters',
    paramLabels: {
      utm_medium: 'Vendor/Medium (utm_medium)',
      utm_source: 'Source (utm_source)',
      utm_term: 'Terms Being Tested (utm_term)',
      utm_content: 'Campaign Content (utm_content)',
      utm_campaign: 'Campaign Name (utm_campaign)',
    },
  },
  custom: {
    name: 'Custom Parameters',
    label: 'Add Custom Parameters',
  },
};

export interface ITrackingParam {
  name: string;
  value: string;
  autoSet: boolean;
  curalateReserved?: boolean;
  // Added by UniversalImageActionsApiService
  label?: string;
}

export interface ITrackingParamGroup {
  id: string;
  inUse: boolean;
  params: ITrackingParam[];
  // Added by UniversalImageActionsApiService
  name?: string;
  // Added by UniversalImageActionsApiService
  label?: string;
}

export interface ITrackingParams {
  paramGroups: ITrackingParamGroup[];
}

interface IRequestRightsGraphCapable {
  isCapable?: boolean;
}

export interface ITrackingParamsResponse {
  versionId?: number;
  trackingParams?: ITrackingParams;
}

export interface ICrawlProductsRequest {
  url: string;
  universalItemId?: PartialCuid;
}

export type ICrawlProductsResponse = IProductSearchResult[];

export interface IProductSearchResult {
  destinationUrl: string;
  images: IProductImage[];
  name?: string;
  productStyleId?: string;
  searchSource: string;
  description?: string;
  price?: string;
  externalId?: string;
  advertiserId?: string;
  productType?: string;
}

export interface IProductImage {
  fileId?: string;
  imageUrl: string;
  sourceImageUrl: string;
}

interface IUniversalRightsRequest {
  requestForRightsMessage: string;
  approvalPhrase: string;
}

/**
 * UniversalImageActionsService
 *
 * Maintains state of the modal and enables intercommunication between
 * the "curalate" app context and the "uia" app context.
 * Uses this.$window to share state.
 */
export default class UniversalImageActionsApiService {
  constructor(
    private $http: IHttpService,
    private $log: ILogService,
    private $window: IWindowService,
    private $upload,
    private ExperimentsService,
    private PromiseService: IPromiseService,
  ) {}

  public getItemMetadataAsync(
    itemId: PartialCuid,
    params?: object,
  ): IPromise<IItemMetadata> {
    if (!itemId) {
      // client_exceptions is showing a bunch of calls to getItemMetadata() with an undefined "itemId".
      // Adding this logging to attempt to catch it (these logs will go to Loggly).
      this.$log.error(
        `Item metadata requested without providing an itemId` +
          `from page=${this.$window.location.href}`,
      );
    }

    const metadataPromise: IAbortablePromise<IItemMetadata> =
      this.PromiseService.processHttpResponse(
        this.$http.get(
          `/api/universal-image-actions/items/${itemId}/metadata`,
          {
            params,
          },
        ),
      );

    // Disregard the rightsRequestStatus response from UIA, use the version from web-gateway, instead
    // this doesn't make a difference for actual accounts but impacts our ability to create demo accounts.
    const rightsStatusPromise = getRightsRequestStatuses([itemId]);

    return Promise.all([metadataPromise, rightsStatusPromise]).then(
      ([metadata, rightsStatusMap]) => {
        return {
          ...metadata,
          rightsRequestStatus: rightsStatusMap.data[itemId],
        };
      },
    );
  }

  public crawlProducts(
    params: ICrawlProductsRequest,
  ): IPromise<ICrawlProductsResponse> {
    return this.PromiseService.processHttpResponse(
      this.$http.get('/api/universal-image-actions/products/crawl', { params }),
    );
  }

  public searchProductsAsync(params: object): IPromise<object> {
    return this.PromiseService.processHttpResponse(
      this.$http.get('/api/universal-image-actions/products', { params }),
      true,
    );
  }

  // Saved the products to the (partial) universal itemId using the UIA endpoint.
  // This function is solely to support the bulk-edit experience.
  public saveProductsAsync(itemId: string, products: object): IPromise<void> {
    const activeExperiments = this.ExperimentsService.getActiveExperiments();
    return this.PromiseService.processHttpResponse(
      this.$http.put(`/api/universal-image-actions/items/${itemId}/products`, {
        products,
        activeExperiments,
      }),
    );
  }

  public uploadProductImageAsync(image: object): IPromise<void> {
    return this.PromiseService.processHttpResponse(
      this.$upload.upload({
        url: '/api/universal-image-actions/products/upload-image',
        method: 'POST',
        file: image,
      }),
    );
  }

  public getTrackingUrlPreview(trackingUrlRequest: object): IPromise<object> {
    return this.PromiseService.processHttpResponse(
      this.$http.post(
        '/api/universal-image-actions/products/tracking-url-preview',
        trackingUrlRequest,
      ),
    );
  }

  public getCurrentTrackingParams(): IPromise<ITrackingParamsResponse> {
    return this.PromiseService.processHttpResponse(
      this.$http.get(
        '/api/universal-image-actions/products/tracking-params-current',
      ),
    );
  }

  public fetchTrackingSettingsByVersion(
    versionId: string,
  ): IPromise<ITrackingParamsResponse> {
    return this.PromiseService.processHttpResponse(
      this.$http.get(
        `/api/universal-image-actions/products/tracking-params/${versionId}`,
      ),
    );
  }

  public getTrackingParametersByVersion(
    versionId: string,
  ): IPromise<ITrackingParamsResponse> {
    return this.fetchTrackingSettingsByVersion(versionId).then((response) => {
      _(response.trackingParams.paramGroups).each(
        (paramGroup: ITrackingParamGroup) => {
          const groupLabels = trackingParamLabels[paramGroup.id] || {
            name: '',
            label: '',
            paramLabels: {},
          };
          paramGroup.name = groupLabels.name;
          paramGroup.label = groupLabels.label;

          _.each(paramGroup.params, (param: ITrackingParam) => {
            param.label =
              (groupLabels.paramLabels &&
                groupLabels.paramLabels[param.name]) ||
              param.name;
          });
        },
      );

      return response;
    });
  }

  public getTagsAsync(itemId: string): IPromise<object> {
    return this.PromiseService.processHttpResponse(
      this.$http.get(`/api/universal-image-actions/items/${itemId}/tags`),
    );
  }

  public saveTagsAsync(itemId: string, tags: object): IPromise<void> {
    const activeExperiments = this.ExperimentsService.getActiveExperiments();
    const params = {
      tags,
      activeExperiments,
    };
    return this.PromiseService.processHttpResponse(
      this.$http.put(
        `/api/universal-image-actions/items/${itemId}/tags`,
        params,
      ),
    );
  }

  public requestRightsAsync(
    itemId: PartialCuid,
    params: IUniversalRightsRequest,
  ): IPromise<IRightsRequestState> {
    return this.PromiseService.processHttpResponse(
      this.$http.post(
        `/api/universal-image-actions/${itemId}/request-rights`,
        params,
      ),
    );
  }

  public addToLibraryAsync(
    itemId: string,
    overrideImageUrl: string,
  ): IPromise<void> {
    const params = {
      overrideImageUrl,
    };
    return this.PromiseService.processHttpResponse(
      this.$http.post(
        `/api/universal-image-actions/items/${itemId}/add-to-library`,
        params,
      ),
    );
  }

  public beautifyImageAsync(
    itemId: string,
    initialImageUrl: string,
    fileId: string,
  ): IPromise<void> {
    const params = {
      imageUrl: initialImageUrl,
      fileId,
    };
    return this.PromiseService.processHttpResponse(
      this.$http.put(
        `/api/universal-image-actions/items/${itemId}/beautify`,
        params,
      ),
    );
  }

  public getVideoDownloadUrl(
    videoId: string,
    muteStatus: boolean,
  ): IPromise<string> {
    return getHighestQualityAvailableVideoUrl(videoId, muteStatus);
  }

  public getVideoDownloadFilename(networkUsername: string = 'curalate') {
    return this.getDownloadFilename(networkUsername, 'video');
  }

  public getImageDownloadFilename(networkUsername: string = 'curalate') {
    return this.getDownloadFilename(networkUsername, 'image');
  }

  public getOriginalUrlAsync(itemId: string): IPromise<object> {
    return this.PromiseService.processHttpResponse(
      this.$http.get(
        `/api/universal-image-actions/items/${itemId}/view-original`,
      ),
    );
  }

  public getRevealEmbedCodeAsync(
    itemId: string,
    params: object,
  ): IPromise<object> {
    return this.PromiseService.processHttpResponse(
      this.$http.get(
        `/api/universal-image-actions/items/${itemId}/embed-code`,
        { params },
      ),
    );
  }

  public getTikTokItem(
    cuid: string,
  ): IPromise<ExistsWithTikTokNetworkItem<IMlsMediaItem> | undefined> {
    return getMediaItemsByCuid([Cuid(cuid)]).then((res) => {
      const item = res[cuid];

      if (isDefinedWithTikTokNetworkItem(item)) {
        return item;
      }

      return undefined;
    });
  }

  public getYouTubeItem(
    cuid: string,
  ): IPromise<ExistsWithYouTubeNetworkItem<IMlsMediaItem> | undefined> {
    return getMediaItemsByCuid([Cuid(cuid)]).then((res) => {
      const item = res[cuid];

      if (isDefinedWithYouTubeNetworkItem(item)) {
        return item;
      }

      return undefined;
    });
  }

  public getUidForHashAsync(params: object): IPromise<IUidResponse> {
    return this.PromiseService.processHttpResponse(
      this.$http.get('/api/universal-image-actions/uid-for-hash', { params }),
    );
  }

  // Initiate rights request for item
  public initiateItemAsync(params: {
    cuid: string;
    approvalPhrase: string;
    requestMessage: string;
    communicationType: RightsRequestCommunicationType;
    requestMessageSource?: RightsRequestMessageSource;
    requestMessageLanguage?: Locales;
    rightsRequestCreationMethod?: RightsRequestCreationMethod;
  }): Promise<{
    itemId: ItemId;
    network: SocialNetwork;
    status: RIGHTS_STATUS;
  }> {
    return initiateRightsRequest(Cuid(params.cuid), {
      approvalPhrase: params.approvalPhrase,
      communicationType: params.communicationType,
      requestMessage: params.requestMessage,
      metadata: {
        requestMessageSource: params.requestMessageSource,
        requestMessageLanguage: params.requestMessageLanguage,
      },
      requestCreationMethod: RightsRequestCreationMethod.ManualCopyPasteFlow,
    });
  }

  public searchBvCatalogProducts(
    params: SamplingCatalogProductsRequest,
  ): Promise<SamplingCatalogProductsResponse> {
    return searchBvCatalogProducts(params);
  }

  public updateBvProductPdpUrl(
    externalId: string,
    pdpUrl: string,
  ): Promise<{ correlationId: string }> {
    return updateBvProductPdpUrl(externalId, pdpUrl);
  }

  private getDownloadFilename(networkUsername: string, mediaType: string) {
    //  Replace any periods or spaces with dashes
    const sanitizedNetworkUsername = networkUsername.replace(
      /\b\.+\b|\b\s+\b/g,
      '-',
    );

    return `${sanitizedNetworkUsername}-${mediaType}-${Date.now()}`;
  }
}

const curalate = angular.module('curalate');

curalate.service('UniversalImageActionsApiService', [
  '$http',
  '$log',
  '$window',
  '$upload',
  'ExperimentsService',
  'PromiseService',
  UniversalImageActionsApiService,
]);
